// Network transport for the authoritative game server.
//
// Owns the WebSocket, the per-connection handshake state, the client-side
// impairment sim (one-way lag/jitter/loss for prediction testing) and the
// upstream/downstream bandwidth meters. No presentation: snapshots, votes,
// kills, chat and markers are handed to the app through the callbacks below.
// The client keeps running its own local (predicted) sim; the transport only
// moves inputs out and server state in.
//
// Wire-object interfaces are generated from core/src/wire.rs; do not hand-edit
// their field lists. `NetItem` adds one client-only render field.
import { NetSim, RateMeter, type NetSimConfig } from "./net_sim";
import type {
  NetPlayerState,
  MatchState,
  NetEntity,
  NetBeam,
  NetItemBase,
  NetHealZone,
  NetFlag,
  NetKill,
} from "./wire_schema";
export type {
  NetPlayerState,
  NetCombat,
  MatchState,
  NetEntity,
  NetBeam,
  NetHealZone,
  NetFlag,
  NetKill,
  NetCapture,
} from "./wire_schema";

export interface NetItem extends NetItemBase {
  /** Local render clock when the item was picked up (for the fade-in ramp). */
  hiddenAt?: number;
}

/** A team/global ping marker from the server (`t: "pingMark"`). */
export interface NetPingMark {
  slot: number;
  team: number;
  o: [number, number, number];
  nick: string;
  kind?: string;
}

/** A replicated projectile/entity (see `core::weapons::Entity`). */
export interface NetSnapshot {
  tick: number;
  players: NetPlayerState[];
  /** Connected-player roster (nick, ping ms, session best ms). */
  roster?: RosterEntry[];
  /** Last command seq the server processed (for reconciliation). */
  ack?: number;
  /** Base64 of the authoritative `PlayerState` after `ack`. */
  ps?: string;
  /** Live projectiles / entities. */
  entities?: NetEntity[];
  /** Live hitscan beams/tracers. */
  beams?: NetBeam[];
  /** This client's accumulated combat event bits (fire/explode/hit/pain/death). */
  ev?: number;
  /** FNV-1a hash of the server's authoritative ruleset. */
  rulesHash?: number;
  /** FNV-1a hash of the server's authoritative game mode. */
  modeHash?: number;
  /** Compact match state (phase, scores, timers). */
  match?: MatchState | null;
  /** Healing Weeball bubbles. */
  heals?: NetHealZone[];
  /** CTF flag states. */
  flags?: NetFlag[];
  /** Damage this client dealt (hit feedback): victim slot, applied, weapon, kill. */
  dmg?: { v: number; a: number; w: number; k?: boolean }[];
}

export interface RosterEntry {
  slot: number;
  nick: string;
  ping: number;
  best: number;
  team?: number;
  frags?: number;
  deaths?: number;
  ready?: boolean;
}

export interface OutCmd {
  s: number; // seq
  f: number; // forward
  r: number; // right
  u: number; // up
  b: number; // buttons
  y: number; // yaw bits
  p: number; // pitch bits
}

/** A vote in progress. `option` is "map" or "collision" (`yes`/`no` are slots). */
export interface NetVote {
  option?: string;
  map?: string;
  value?: boolean;
  /** Ruleset vote label + the proposed rules. */
  label?: string;
  rules?: Record<string, number>;
  /** Game-mode vote: canonical mode dump. */
  mode?: string;
  /** Vote pass threshold (percent). */
  percent?: number;
  by?: number;
  yes?: number[];
  no?: number[];
  timeoutMs?: number;
}

export interface NetVoteResult {
  passed: boolean;
  option?: string;
  map?: string;
  value?: boolean;
  label?: string;
  reason?: string;
}

export class NetTransport {
  private ws: WebSocket | null = null;
  private upSim: NetSim<string> | null = null;
  private downSim: NetSim<NetSnapshot> | null = null;
  private readonly upMeter = new RateMeter();
  private readonly downMeter = new RateMeter();
  /** Our authoritative slot, or -1 until the welcome arrives. */
  slot = -1;
  spawns = 0;
  /** The server's map name (authoritative), or null. */
  map: string | null = null;
  /** Player-vs-player collision (server option). */
  collision = false;
  /** Short-lived HMAC ticket for `/api/times` (issued after `hello`). */
  finishTicket = "";
  /** Whether the server accepted our admin token (live ruleset tuning). */
  admin = false;
  /** Server's simulation tick rate (Hz), or 0 if the server didn't advertise it. */
  tickRate = 0;
  /** Server's wire/snapshot protocol version, or 0 if unversioned/old. */
  protocolVersion = 0;
  /** Server's per-client snapshot stride (Option 2), or 0 if unknown. */
  snapEvery = 0;
  /** Server's authoritative mode dump/label/hash. */
  modeDump = "";
  modeHash = 0;
  modeLabel = "";
  /** Vote threshold reported by the server (percent). */
  votePercent = 75;
  /** Current map-item list (welcome + deltas). */
  items: NetItem[] = [];

  /** Items that arrive already taken (join mid-respawn) start their fade ramp now. */
  private markHiddenItems(): void {
    const now = performance.now();
    for (const it of this.items) {
      if (!it.p && it.hiddenAt === undefined) it.hiddenAt = now;
    }
  }

  onWelcome: ((slot: number, spawns: number, spawn: [number, number, number, number] | null) => void) | null = null;
  /** Delivered after the impairment delay (if any). */
  onSnapshot: ((snap: NetSnapshot) => void) | null = null;
  onVote: ((v: NetVote) => void) | null = null;
  onVoteResult: ((r: NetVoteResult) => void) | null = null;
  onOptions: ((o: { collision: boolean }) => void) | null = null;
  /** The server sent its authoritative ruleset (`rulesState`). */
  onRulesState: ((r: { rules: Record<string, number>; rulesHash: number }) => void) | null = null;
  /** The server sent its authoritative game mode (`welcome`/`modeState`). */
  onModeState: ((r: { mode: string; label: string; modeHash: number }) => void) | null = null;
  /** Full item list (welcome / mode change). */
  onItems: ((items: NetItem[]) => void) | null = null;
  /** Item-presence deltas: flat `[index, present, ...]`. */
  onItemDelta: ((changes: number[]) => void) | null = null;
  /** Deaths since the last snapshot (kill feed). */
  onKills: ((kills: NetKill[]) => void) | null = null;
  /** CTF captures. */
  onCaptures: ((captures: { team: number; slot: number }[]) => void) | null = null;
  /** A chat line: `nick` (empty for system messages), `text`, and `sys`. */
  onChat: ((c: { nick: string; text: string; sys: boolean; slot: number }) => void) | null = null;
  /** A relayed ping/tag marker (team-only, or global in race/freestyle). */
  onPingMark: ((p: NetPingMark) => void) | null = null;
  onClose: (() => void) | null = null;
  onAdminState: ((admin: boolean) => void) | null = null;

  /** Server-assigned spawn `[x, y, z, yaw]` (radians), or null. */
  spawn: [number, number, number, number] | null = null;

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect(url: string, snapStride: number): void {
    if (this.ws) return;
    // Request a per-connection snapshot stride (Option 2). If the server honours
    // it, it echoes `snapEvery` in the welcome and the app aligns its cadence.
    const wsUrl = url + (url.includes("?") ? "&" : "?") + `snap=${snapStride}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      this.onClose?.();
      return;
    }
    this.ws = ws;
    ws.addEventListener("message", (e) => {
      let m: { t?: string } & Record<string, unknown>;
      try {
        m = JSON.parse(e.data as string);
      } catch {
        return;
      }
      if (m.t === "ping") {
        ws.send(JSON.stringify({ t: "pong", ts: m.ts }));
        return;
      }
      if (m.t === "welcome") {
        this.slot = Number(m.slot ?? -1);
        this.finishTicket = "";
        this.spawns = Number(m.spawns ?? 0);
        this.map = typeof m.map === "string" ? m.map : null;
        this.collision = m.collision === true;
        this.tickRate = Number(m.tickRate ?? 0) || 0;
        this.protocolVersion = Number(m.protocolVersion ?? 0) || 0;
        this.snapEvery = Number(m.snapEvery ?? 0) || 0;
        this.votePercent = Number(m.votePercent ?? 75) || 75;
        const mo = m.mode as { dump?: string; label?: string; hash?: number } | undefined;
        if (mo && typeof mo.dump === "string") {
          this.modeDump = mo.dump;
          this.modeHash = Number(mo.hash ?? 0) || 0;
          this.modeLabel = typeof mo.label === "string" ? mo.label : "";
          this.onModeState?.({ mode: this.modeDump, label: this.modeLabel, modeHash: this.modeHash });
        }
        const wr = m.rules as Record<string, number> | undefined;
        if (wr && typeof wr === "object") {
          this.onRulesState?.({ rules: wr, rulesHash: Number(m.rulesHash ?? 0) || 0 });
        }
        if (Array.isArray(m.items)) {
          this.items = (m.items as NetItem[]).filter((it) => it && Array.isArray(it.o));
          this.markHiddenItems();
          this.onItems?.(this.items);
        }
        const sp = Array.isArray(m.spawn) ? (m.spawn as number[]) : null;
        this.spawn = sp && sp.length >= 4 ? [sp[0], sp[1], sp[2], sp[3]] : null;
        this.onWelcome?.(this.slot, this.spawns, this.spawn);
      } else if (m.t === "ticket") {
        this.finishTicket = typeof m.value === "string" ? m.value : "";
      } else if (m.t === "adminState") {
        this.admin = m.admin === true;
        this.onAdminState?.(this.admin);
      } else if (m.t === "snap") {
        const bytes = typeof e.data === "string" ? e.data.length : 0;
        const now = performance.now();
        this.downMeter.add(bytes, now);
        const snap = m as unknown as NetSnapshot;
        if (this.downSim) this.downSim.push(snap, now);
        else this.onSnapshot?.(snap);
      } else if (m.t === "vote") {
        this.onVote?.(m as unknown as NetVote);
      } else if (m.t === "voteResult") {
        this.onVoteResult?.(m as unknown as NetVoteResult);
      } else if (m.t === "options") {
        this.collision = (m as { collision?: boolean }).collision === true;
        this.onOptions?.({ collision: this.collision });
      } else if (m.t === "modeState") {
        const mm = m as { mode?: string; label?: string; modeHash?: number };
        if (typeof mm.mode === "string") {
          this.modeDump = mm.mode;
          this.modeHash = Number(mm.modeHash ?? 0) || 0;
          this.modeLabel = typeof mm.label === "string" ? mm.label : "";
          const mi = (m as { items?: NetItem[] }).items;
          if (Array.isArray(mi)) {
            this.items = mi;
            this.markHiddenItems();
            this.onItems?.(this.items);
          }
          this.onModeState?.({ mode: this.modeDump, label: this.modeLabel, modeHash: this.modeHash });
        }
      } else if (m.t === "items") {
        const c = (m as { c?: number[] }).c;
        if (Array.isArray(c)) {
          const now = performance.now();
          for (let i = 0; i + 1 < c.length; i += 2) {
            const idx = c[i] | 0;
            const it = this.items[idx];
            if (!it) continue;
            const present = c[i + 1] > 0.5;
            if (it.p && !present) it.hiddenAt = now;
            else if (!it.p && present) it.hiddenAt = undefined;
            it.p = present;
          }
          this.onItemDelta?.(c);
        }
      } else if (m.t === "kills") {
        const k = (m as { k?: NetKill[] }).k;
        if (Array.isArray(k)) this.onKills?.(k);
      } else if (m.t === "captures") {
        const c = (m as { c?: { team: number; slot: number }[] }).c;
        if (Array.isArray(c)) this.onCaptures?.(c);
      } else if (m.t === "rulesState") {
        const mm = m as { rules?: Record<string, number>; rulesHash?: number };
        if (mm.rules && typeof mm.rules === "object") {
          this.onRulesState?.({ rules: mm.rules, rulesHash: Number(mm.rulesHash ?? 0) });
        }
      } else if (m.t === "chat") {
        const c = m as { nick?: string; text?: string; sys?: boolean; slot?: number };
        this.onChat?.({
          nick: typeof c.nick === "string" ? c.nick : "",
          text: typeof c.text === "string" ? c.text : "",
          sys: c.sys === true,
          slot: Number(c.slot ?? -1),
        });
      } else if (m.t === "pingMark") {
        const p = m as { slot?: number; team?: number; o?: number[]; nick?: string; kind?: string };
        if (Array.isArray(p.o) && p.o.length >= 3) {
          this.onPingMark?.({
            slot: Number(p.slot ?? -1),
            team: Number(p.team ?? 0),
            o: [Number(p.o[0]), Number(p.o[1]), Number(p.o[2])],
            nick: typeof p.nick === "string" ? p.nick : "",
            kind: typeof p.kind === "string" ? p.kind : "look",
          });
        }
      }
    });
    ws.addEventListener("close", () => {
      this.ws = null;
      this.slot = -1;
      this.onClose?.();
    });
    ws.addEventListener("error", () => {
      /* close fires next */
    });
  }

  /** Select a weapon (`strong` = primary fire mode). */
  selectWeapon(weapon: number, strong = true): boolean {
    return this.sendText(JSON.stringify({ t: "weapon", w: weapon, strong }));
  }

  /** Ask the server for its authoritative ruleset. */
  requestRules(): boolean {
    return this.sendText(JSON.stringify({ t: "getRules" }));
  }

  /** Propose a ruleset vote (`label` is the profile/preset name). */
  proposeRules(rules: Record<string, number>, label: string): boolean {
    return this.sendText(JSON.stringify({ t: "callvote", rules, label }));
  }

  /** Ask the server for its authoritative game mode. */
  requestMode(): boolean {
    return this.sendText(JSON.stringify({ t: "getMode" }));
  }

  /** Propose a mode package (mode dump + rules) as a vote. */
  proposeMode(mode: string, rules: Record<string, number>, label: string): boolean {
    return this.sendText(JSON.stringify({ t: "callvote", mode, rules, label }));
  }

  /** Send a pre-serialized message. Returns whether it was sent. */
  sendText(text: string): boolean {
    if (!this.connected) return false;
    this.ws!.send(text);
    return true;
  }

  /** Send a chat line (temporary text chat). */
  say(text: string): boolean {
    return this.sendText(JSON.stringify({ t: "say", text }));
  }

  close(): void {
    this.ws?.close();
    this.ws = null;
  }

  /**
   * Enable (or clear) the client-side impairment sim. Both directions share one
   * config; outgoing command batches and incoming snapshots are delayed/dropped.
   */
  setImpairment(cfg: NetSimConfig | null): void {
    if (cfg) {
      this.upSim = new NetSim<string>(cfg);
      this.downSim = new NetSim<NetSnapshot>(cfg);
    } else {
      this.upSim = null;
      this.downSim = null;
    }
  }

  /** Queue a command batch (impaired) or send it immediately. */
  sendCommands(text: string, now: number): void {
    if (this.upSim) this.upSim.push(text, now);
    else if (this.sendText(text)) this.upMeter.add(text.length, now);
  }

  /** Deliver impairment-delayed traffic that is due by `now`. */
  flush(now: number): void {
    if (this.upSim && this.connected) {
      const due: string[] = [];
      this.upSim.drain(now, due);
      for (const text of due) {
        if (this.sendText(text)) this.upMeter.add(text.length, now);
      }
    }
    if (this.downSim) {
      const due: NetSnapshot[] = [];
      this.downSim.drain(now, due);
      for (const s of due) this.onSnapshot?.(s);
    }
  }

  upBytesPerSec(now: number): number {
    return this.upMeter.bytesPerSec(now);
  }

  downBytesPerSec(now: number): number {
    return this.downMeter.bytesPerSec(now);
  }

  upMsgsPerSec(now: number): number {
    return this.upMeter.msgsPerSec(now);
  }

  downMsgsPerSec(now: number): number {
    return this.downMeter.msgsPerSec(now);
  }
}
